// Trade Dialogs Component
// Modals for closing, activating, and resetting trades
import React from 'react';
import { 
  Target, 
  Play, 
  AlertTriangle, 
  Calendar,
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Activity, 
  CheckCircle 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

// =====================
// Close Trade Dialog
// =====================
export const CloseTradeDialog = ({
  language,
  trade,
  exitPrice,
  setExitPrice,
  exitLevel,
  setExitLevel,
  currentPrices,
  onConfirm,
  onCancel
}) => {
  if (!trade) return null;

  const calculatePnl = () => {
    const entry = trade.entry_price;
    const exit = parseFloat(exitPrice);
    const marge = trade.position_size || 0;
    const pnlPct = trade.direction === 'LONG'
      ? ((exit - entry) / entry) * 100 * (trade.leverage || 1)
      : ((entry - exit) / entry) * 100 * (trade.leverage || 1);
    const pnlDollars = (pnlPct / 100) * marge;
    return { pnlPct, pnlDollars };
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-md bg-card border-border my-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-5 h-5" />
            {language === 'fr' ? 'Clôturer le Trade' : 'Close Trade'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Trade Info */}
          <div className="p-3 rounded-lg bg-secondary/30">
            <div className="font-mono font-bold">{trade.symbol}</div>
            <div className="text-sm text-muted-foreground">
              {trade.direction} | {language === 'fr' ? 'Entrée' : 'Entry'}: ${trade.entry_price}
              {trade.is_paper && <span className="ml-2 text-purple-400">(PAPER)</span>}
            </div>
          </div>
          
          {/* Exit Price Input */}
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Prix de sortie' : 'Exit Price'}</Label>
            <Input
              type="number"
              step="0.01"
              value={exitPrice}
              onChange={(e) => setExitPrice(e.target.value)}
              placeholder="0.00"
              className="font-mono"
              data-testid="exit-price-input"
            />
          </div>
          
          {/* Exit Level Selector - WIN only */}
          {trade.targetStatus === 'WIN' && (
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Niveau de sortie' : 'Exit Level'}</Label>
              <div className="grid grid-cols-3 gap-2">
                {['tp1', 'tp2', 'tp100'].map((level) => (
                  <Button
                    key={level}
                    type="button"
                    variant={exitLevel === level ? 'default' : 'outline'}
                    onClick={() => setExitLevel(level)}
                    className={exitLevel === level ? 'bg-primary' : ''}
                    data-testid={`exit-level-${level}`}
                  >
                    {level === 'tp1' ? 'TP1 (25%)' : level === 'tp2' ? 'TP2 (50%)' : 'TP100 (100%)'}
                  </Button>
                ))}
              </div>
            </div>
          )}
          
          {/* Quick Price Buttons - LOSS only */}
          {trade.targetStatus === 'LOSS' && (
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Prix rapide' : 'Quick Price'}</Label>
              <div className="grid grid-cols-3 gap-2">
                {currentPrices[trade.symbol] && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setExitPrice(currentPrices[trade.symbol].toString())}
                    className="border-yellow-500/30 text-yellow-400 text-xs flex-col h-auto py-2"
                    data-testid="quick-price-market"
                  >
                    <span>{language === 'fr' ? 'Marché' : 'Market'}</span>
                    <span className="text-xs opacity-70">${currentPrices[trade.symbol]?.toFixed(2)}</span>
                  </Button>
                )}
                {trade.stop_loss && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setExitPrice(trade.stop_loss.toString())}
                    className="border-red-500/30 text-red-400 text-xs flex-col h-auto py-2"
                    data-testid="quick-price-sl"
                  >
                    <span>SL</span>
                    <span className="text-xs opacity-70">${trade.stop_loss?.toFixed(2)}</span>
                  </Button>
                )}
                {trade.liquidation_price && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setExitPrice(trade.liquidation_price.toString())}
                    className="border-orange-500/30 text-orange-400 text-xs flex-col h-auto py-2"
                    data-testid="quick-price-liq"
                  >
                    <span>Liq.</span>
                    <span className="text-xs opacity-70">${trade.liquidation_price?.toFixed(2)}</span>
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {/* PnL Preview */}
          {exitPrice && trade.entry_price && (
            <div className="p-3 rounded-lg bg-secondary/20">
              <div className="text-sm text-muted-foreground mb-1">PNL estimé</div>
              {(() => {
                const { pnlPct, pnlDollars } = calculatePnl();
                return (
                  <div className={`font-mono font-bold ${pnlPct >= 0 ? 'text-primary' : 'text-destructive'}`}>
                    {pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}% 
                    <span className="text-sm ml-2">({pnlDollars >= 0 ? '+' : ''}{pnlDollars.toFixed(2)}$)</span>
                  </div>
                );
              })()}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} className="flex-1">
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={onConfirm}
              disabled={!exitPrice}
              className={`flex-1 ${trade.targetStatus === 'WIN' ? 'bg-primary hover:bg-primary/90' : 'bg-destructive hover:bg-destructive/90'}`}
              data-testid="confirm-close-btn"
            >
              {trade.targetStatus === 'WIN' 
                ? (language === 'fr' ? 'Clôturer Gagnant' : 'Close as Win')
                : (language === 'fr' ? 'Clôturer Perdant' : 'Close as Loss')
              }
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// =====================
// Activate Trade Dialog
// =====================
export const ActivateTradeDialog = ({
  language,
  trade,
  activationPrice,
  setActivationPrice,
  onConfirm,
  onCancel
}) => {
  if (!trade) return null;

  const calculateParams = () => {
    const newEntry = parseFloat(activationPrice);
    const sl = trade.stop_loss;
    const tp1 = trade.take_profit_1;
    const tp2 = trade.take_profit_2;
    const tp100 = trade.take_profit_100;
    const marge = trade.position_size || 0;
    const leverage = trade.leverage || 1;
    const isLong = trade.direction === 'LONG';
    
    const slDist = newEntry > 0 ? Math.abs(sl - newEntry) / newEntry * 100 : 0;
    const tp100Dist = newEntry > 0 && tp100 ? Math.abs(tp100 - newEntry) / newEntry * 100 : 0;
    const rr = slDist > 0 ? tp100Dist / slDist : 0;
    
    const perteSL = (slDist / 100) * marge * leverage;
    const gainTp1 = tp1 && newEntry > 0 ? (Math.abs(tp1 - newEntry) / newEntry * 100 / 100) * marge * leverage : 0;
    const gainTp2 = tp2 && newEntry > 0 ? (Math.abs(tp2 - newEntry) / newEntry * 100 / 100) * marge * leverage : 0;
    const gainTp100 = tp100 && newEntry > 0 ? (Math.abs(tp100 - newEntry) / newEntry * 100 / 100) * marge * leverage : 0;
    
    const isValidEntry = isLong 
      ? (newEntry < tp100 && newEntry > sl)
      : (newEntry > tp100 && newEntry < sl);
    
    return { rr, perteSL, gainTp1, gainTp2, gainTp100, isValidEntry, tp1, tp2, tp100 };
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-md bg-card border-yellow-500/30 my-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-yellow-400">
            <Play className="w-5 h-5" />
            {language === 'fr' ? 'Activer le Trade' : 'Activate Trade'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Trade Info */}
          <div className="p-3 rounded-lg bg-secondary/30">
            <div className="font-mono font-bold">{trade.symbol}</div>
            <div className="text-sm text-muted-foreground">
              {trade.direction} | {language === 'fr' ? 'Entrée prévue' : 'Planned Entry'}: ${trade.entry_price}
              {trade.is_paper && <span className="ml-2 text-purple-400">(PAPER)</span>}
            </div>
          </div>
          
          {/* Entry Price Input */}
          <div className="space-y-2">
            <Label>{language === 'fr' ? 'Prix d\'entrée réel' : 'Actual Entry Price'}</Label>
            <Input
              type="number"
              step="0.01"
              value={activationPrice}
              onChange={(e) => setActivationPrice(e.target.value)}
              placeholder="0.00"
              className="font-mono"
              data-testid="activation-price-input"
            />
          </div>
          
          {/* Recalculated Parameters */}
          {activationPrice && (
            <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
              <div className="text-sm font-medium text-yellow-400 mb-2">
                {language === 'fr' ? 'Paramètres recalculés' : 'Recalculated Parameters'}
              </div>
              {(() => {
                const params = calculateParams();
                return (
                  <div className="space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-muted-foreground">R:R :</span>
                        <span className={`ml-2 font-mono font-bold ${params.rr >= 2 ? 'text-primary' : 'text-orange-400'}`}>
                          {params.rr.toFixed(2)}:1
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Perte SL :</span>
                        <span className="ml-2 font-mono text-destructive">-${params.perteSL.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {params.tp1 && (
                        <div>
                          <span className="text-xs text-muted-foreground">TP1</span>
                          <div className="font-mono text-primary text-xs">+${params.gainTp1.toFixed(2)}</div>
                        </div>
                      )}
                      {params.tp2 && (
                        <div>
                          <span className="text-xs text-muted-foreground">TP2</span>
                          <div className="font-mono text-primary text-xs">+${params.gainTp2.toFixed(2)}</div>
                        </div>
                      )}
                      {params.tp100 && (
                        <div>
                          <span className="text-xs text-muted-foreground">TP100</span>
                          <div className="font-mono text-primary text-xs font-bold">+${params.gainTp100.toFixed(2)}</div>
                        </div>
                      )}
                    </div>
                    {!params.isValidEntry && (
                      <div className="mt-2 p-2 rounded bg-red-500/20 border border-red-500/30">
                        <div className="flex items-center gap-2 text-red-400 text-xs">
                          <AlertTriangle className="w-4 h-4" />
                          {language === 'fr' 
                            ? 'Attention : Le prix d\'entrée est invalide par rapport aux niveaux SL/TP' 
                            : 'Warning: Entry price is invalid relative to SL/TP levels'}
                        </div>
                      </div>
                    )}
                    {params.rr < 2 && params.isValidEntry && (
                      <div className="mt-2 p-2 rounded bg-orange-500/20 border border-orange-500/30">
                        <div className="flex items-center gap-2 text-orange-400 text-xs">
                          <AlertTriangle className="w-4 h-4" />
                          {language === 'fr' 
                            ? 'Ratio R:R inférieur à 2:1 - Trade risqué' 
                            : 'R:R ratio below 2:1 - Risky trade'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} className="flex-1">
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            <Button 
              onClick={onConfirm}
              disabled={!activationPrice}
              className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
              data-testid="confirm-activate-btn"
            >
              <Play className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Activer le Trade' : 'Activate Trade'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// =====================
// Reset Portfolio Dialog
// =====================
export const ResetPortfolioDialog = ({
  language,
  open,
  onOpenChange,
  confirmText,
  setConfirmText,
  onConfirm,
  isLoading
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-red-500/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <AlertTriangle className="w-5 h-5" />
            {language === 'fr' ? 'Réinitialiser le Portfolio Paper' : 'Reset Paper Portfolio'}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {language === 'fr' 
              ? 'Cette action est irréversible. Tous vos trades paper seront définitivement supprimés et votre capital sera réinitialisé à $10,000.'
              : 'This action is irreversible. All your paper trades will be permanently deleted and your capital will be reset to $10,000.'
            }
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
            <p className="text-sm text-red-400 font-medium mb-2">
              {language === 'fr' 
                ? 'Pour confirmer, tapez RESET ci-dessous :'
                : 'To confirm, type RESET below:'
              }
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              placeholder="RESET"
              className="bg-background border-red-500/30 text-center font-mono text-lg tracking-widest"
              data-testid="reset-confirm-input"
            />
          </div>
        </div>
        
        <DialogFooter className="gap-2">
          <Button 
            variant="outline" 
            onClick={() => {
              onOpenChange(false);
              setConfirmText('');
            }}
          >
            {language === 'fr' ? 'Annuler' : 'Cancel'}
          </Button>
          <Button 
            variant="destructive"
            onClick={onConfirm}
            disabled={confirmText !== 'RESET' || isLoading}
            data-testid="reset-confirm-btn"
          >
            {isLoading 
              ? (language === 'fr' ? 'Réinitialisation...' : 'Resetting...')
              : (language === 'fr' ? 'Réinitialiser' : 'Reset')
            }
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// =====================
// PnL History Dialog
// =====================
export const PnLHistoryDialog = ({
  language,
  dialogType,
  onClose,
  trades,
  calculateTotals,
  currentPrices,
  getDisplayName,
  formatPrice
}) => {
  if (!dialogType) return null;

  const totals = calculateTotals(trades);
  
  // Filter trades based on dialog type
  let tradesForDialog = [];
  if (dialogType === 'gains') {
    tradesForDialog = trades.filter(t => 
      (t.status === 'WIN' || t.status === 'LOSS') && (t.pnl || 0) > 0
    );
  } else if (dialogType === 'losses') {
    tradesForDialog = trades.filter(t => 
      (t.status === 'WIN' || t.status === 'LOSS') && (t.pnl || 0) < 0
    );
  } else if (dialogType === 'total') {
    tradesForDialog = trades.filter(t => 
      t.status === 'WIN' || t.status === 'LOSS'
    );
  } else if (dialogType === 'unrealized') {
    tradesForDialog = trades.filter(t => t.status === 'active');
  } else if (dialogType === 'partial') {
    tradesForDialog = trades.filter(t => {
      const warnings = t.warnings_sent || {};
      return (t.status === 'active' || t.status === 'pending') && 
             (warnings.tp1_hit || warnings.tp2_hit);
    });
  }
  
  // Sort by date (most recent first)
  tradesForDialog.sort((a, b) => {
    const dateA = new Date(a.closed_at || a.created_at || 0);
    const dateB = new Date(b.closed_at || b.created_at || 0);
    return dateB - dateA;
  });

  const getDialogTitle = () => {
    switch (dialogType) {
      case 'gains':
        return (
          <>
            <TrendingUp className="w-5 h-5 text-green-400" />
            {language === 'fr' ? 'Gains Total (Clôturés)' : 'Total Gains (Closed)'}
          </>
        );
      case 'losses':
        return (
          <>
            <TrendingDown className="w-5 h-5 text-red-400" />
            {language === 'fr' ? 'Pertes Total (Clôturées)' : 'Total Losses (Closed)'}
          </>
        );
      case 'total':
        return (
          <>
            <DollarSign className="w-5 h-5 text-primary" />
            {language === 'fr' ? 'PnL Réalisé Total' : 'Total Realized PnL'}
          </>
        );
      case 'unrealized':
        return (
          <>
            <Activity className="w-5 h-5 text-blue-400" />
            {language === 'fr' ? 'PnL Non Réalisé' : 'Unrealized PnL'}
          </>
        );
      case 'partial':
        return (
          <>
            <Target className="w-5 h-5 text-emerald-400" />
            {language === 'fr' ? 'Gains Partiels (TP touchés)' : 'Partial Gains (TP hits)'}
          </>
        );
      default:
        return null;
    }
  };

  const getDialogDescription = () => {
    switch (dialogType) {
      case 'gains': return language === 'fr' ? 'Trades clôturés avec profit' : 'Trades closed with profit';
      case 'losses': return language === 'fr' ? 'Trades clôturés avec perte' : 'Trades closed with loss';
      case 'total': return language === 'fr' ? 'Tous les trades clôturés' : 'All closed trades';
      case 'unrealized': return language === 'fr' ? 'Position restante non sécurisée' : 'Remaining unsecured position';
      case 'partial': return language === 'fr' ? 'TP touchés sur trades actifs (gains sécurisés)' : 'TP hits on active trades (secured gains)';
      default: return '';
    }
  };

  const getPnlValue = () => {
    switch (dialogType) {
      case 'gains': return `+$${totals.closedGains.toFixed(2)}`;
      case 'losses': return `-$${totals.closedLosses.toFixed(2)}`;
      case 'total': return `${totals.realizedPnl >= 0 ? '+' : ''}$${totals.realizedPnl.toFixed(2)}`;
      case 'unrealized': return `${totals.unrealizedPnl >= 0 ? '+' : ''}$${totals.unrealizedPnl.toFixed(2)}`;
      case 'partial': return `+$${totals.partialTpGains.toFixed(2)}`;
      default: return '';
    }
  };

  const getPnlColorClass = () => {
    switch (dialogType) {
      case 'gains': return 'text-green-400';
      case 'losses': return 'text-red-400';
      case 'partial': return 'text-emerald-400';
      case 'unrealized': return totals.unrealizedPnl >= 0 ? 'text-blue-400' : 'text-orange-400';
      case 'total': return totals.realizedPnl >= 0 ? 'text-primary' : 'text-destructive';
      default: return '';
    }
  };

  return (
    <Dialog open={!!dialogType} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription>
            {getDialogDescription()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {tradesForDialog.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {language === 'fr' ? 'Aucun trade dans cette catégorie' : 'No trades in this category'}
            </div>
          ) : (
            <div className="space-y-2">
              {/* Summary Header */}
              <div className="p-3 rounded-lg bg-card/50 border border-white/10 mb-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Nombre' : 'Count'}</div>
                    <div className="font-bold text-lg">{tradesForDialog.length}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">
                      {dialogType === 'unrealized' && (language === 'fr' ? 'PnL Latent' : 'Unrealized PnL')}
                      {dialogType === 'partial' && (language === 'fr' ? 'Gains Sécurisés' : 'Secured Gains')}
                      {(dialogType === 'gains' || dialogType === 'losses' || dialogType === 'total') && (language === 'fr' ? 'PnL Total' : 'Total PnL')}
                    </div>
                    <div className={`font-bold text-lg font-mono ${getPnlColorClass()}`}>
                      {getPnlValue()}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground">{language === 'fr' ? 'Marge Totale' : 'Total Margin'}</div>
                    <div className="font-bold text-lg font-mono">
                      ${tradesForDialog.reduce((sum, t) => sum + (t.position_size || 0), 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Trades List */}
              {tradesForDialog.map((trade) => {
                const warnings = trade.warnings_sent || {};
                const pnl = trade.pnl || 0;
                const pnlPct = trade.pnl_percentage || 0;
                const isPositive = pnl >= 0;
                const currentPrice = currentPrices[trade.symbol];
                
                const tp1Pnl = warnings.tp1_partial_pnl || 0;
                const tp2Pnl = warnings.tp2_partial_pnl || 0;
                const tp1Pct = warnings.tp1_pnl_pct || 0;
                const tp2Pct = warnings.tp2_pnl_pct || 0;
                
                let displayPnl = pnl;
                let displayPnlPct = pnlPct;
                if (trade.status === 'active' && currentPrice && trade.entry_price) {
                  const isLong = trade.direction === 'LONG';
                  const leverage = trade.leverage || 1;
                  const posSize = trade.position_size || 0;
                  if (isLong) {
                    displayPnlPct = ((currentPrice - trade.entry_price) / trade.entry_price) * 100 * leverage;
                  } else {
                    displayPnlPct = ((trade.entry_price - currentPrice) / trade.entry_price) * 100 * leverage;
                  }
                  displayPnl = (displayPnlPct / 100) * posSize;
                }
                
                return (
                  <div 
                    key={trade.id}
                    className={`p-3 rounded-lg border transition-colors ${
                      dialogType === 'unrealized' 
                        ? 'bg-blue-500/5 border-blue-500/20'
                        : isPositive 
                          ? 'bg-green-500/5 border-green-500/20' 
                          : 'bg-red-500/5 border-red-500/20'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          trade.direction === 'LONG' ? 'bg-green-500/20' : 'bg-red-500/20'
                        }`}>
                          {trade.direction === 'LONG' 
                            ? <TrendingUp className="w-4 h-4 text-green-400" />
                            : <TrendingDown className="w-4 h-4 text-red-400" />
                          }
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{getDisplayName(trade.symbol, language)}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              trade.direction === 'LONG' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                            }`}>
                              {trade.direction}
                            </span>
                            <span className="text-xs text-muted-foreground">x{trade.leverage}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {trade.closed_at 
                              ? new Date(trade.closed_at).toLocaleDateString()
                              : new Date(trade.created_at).toLocaleDateString()
                            }
                            {' • '}
                            {language === 'fr' ? 'Marge' : 'Margin'}: ${trade.position_size?.toFixed(0)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className={`font-mono font-bold ${
                          displayPnl >= 0 ? 'text-green-400' : 'text-red-400'
                        }`}>
                          {displayPnl >= 0 ? '+' : ''}{displayPnl.toFixed(2)}$
                        </div>
                        <div className={`text-xs ${
                          displayPnlPct >= 0 ? 'text-green-400/70' : 'text-red-400/70'
                        }`}>
                          {displayPnlPct >= 0 ? '+' : ''}{displayPnlPct.toFixed(1)}%
                        </div>
                      </div>
                    </div>
                    
                    {/* Price Info */}
                    <div className="mt-2 pt-2 border-t border-white/5 grid grid-cols-3 gap-2 text-xs">
                      <div>
                        <span className="text-muted-foreground">{language === 'fr' ? 'Entrée' : 'Entry'}:</span>
                        <span className="ml-1 font-mono">${trade.entry_price?.toFixed(2)}</span>
                      </div>
                      {trade.exit_price && (
                        <div>
                          <span className="text-muted-foreground">{language === 'fr' ? 'Sortie' : 'Exit'}:</span>
                          <span className="ml-1 font-mono">${trade.exit_price?.toFixed(2)}</span>
                        </div>
                      )}
                      {trade.status === 'active' && currentPrice && (
                        <div>
                          <span className="text-muted-foreground">{language === 'fr' ? 'Actuel' : 'Current'}:</span>
                          <span className="ml-1 font-mono">${formatPrice(currentPrice)}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* TP Hits Info */}
                    {(warnings.tp1_hit || warnings.tp2_hit) && (
                      <div className="mt-2 pt-2 border-t border-white/5 flex flex-wrap gap-3 text-xs">
                        {warnings.tp1_hit && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-green-500/10 border border-green-500/20">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            <span className="text-green-400 font-medium">TP1</span>
                            <span className="text-green-400 font-mono">+${tp1Pnl.toFixed(2)}</span>
                            <span className="text-green-400/60">(+{tp1Pct.toFixed(1)}%)</span>
                          </div>
                        )}
                        {warnings.tp2_hit && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-green-500/10 border border-green-500/20">
                            <CheckCircle className="w-3 h-3 text-green-400" />
                            <span className="text-green-400 font-medium">TP2</span>
                            <span className="text-green-400 font-mono">+${tp2Pnl.toFixed(2)}</span>
                            <span className="text-green-400/60">(+{tp2Pct.toFixed(1)}%)</span>
                          </div>
                        )}
                        {(tp1Pnl > 0 || tp2Pnl > 0) && (
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-500/10 border border-emerald-500/20">
                            <span className="text-emerald-400 font-medium">=</span>
                            <span className="text-emerald-400 font-mono font-bold">+${(tp1Pnl + tp2Pnl).toFixed(2)}</span>
                            <span className="text-emerald-400/60">{language === 'fr' ? 'sécurisé' : 'secured'}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {language === 'fr' ? 'Fermer' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default {
  CloseTradeDialog,
  ActivateTradeDialog,
  ResetPortfolioDialog,
  PnLHistoryDialog
};
